/**
 * Cookie Consent Popup Component
 * Shows on first visit and stores preference in localStorage
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, Shield, Settings } from "lucide-react";

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    performance: true,
    functional: true,
    targeting: false,
  });

  useEffect(() => {
    // Check if user has already set cookie preferences
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      // Show popup after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      performance: true,
      functional: true,
      targeting: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookie_consent", JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    const selectedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookie_consent", JSON.stringify(selectedPreferences));
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      performance: false,
      functional: false,
      targeting: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookie_consent", JSON.stringify(essentialOnly));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]" />

      {/* Cookie Popup */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6" data-testid="cookie-consent-popup">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Main Content */}
          {!showSettings ? (
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#48d480]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#48d480]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#0f2d52] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    We Value Your Privacy
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or 
                    read more about our{" "}
                    <Link to="/cookie-policy" className="text-[#48d480] hover:underline font-medium">
                      Cookie Policy
                    </Link>.
                  </p>

                  {/* Quick Info */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Shield className="w-4 h-4 text-[#48d480]" />
                      <span>Your data is secure</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Settings className="w-4 h-4 text-[#48d480]" />
                      <span>Customizable preferences</span>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="flex-1 bg-[#48d480] hover:bg-[#3bc070] text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      data-testid="cookie-accept-all"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex-1 bg-[#0f2d52] hover:bg-[#1a3a5c] text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      data-testid="cookie-customize"
                    >
                      Customize
                    </button>
                    <button
                      onClick={handleRejectNonEssential}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      data-testid="cookie-reject"
                    >
                      Essential Only
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#0f2d52]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-[#0f2d52]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Essential Cookies
                    </h4>
                    <p className="text-sm text-gray-500">Required for the website to function properly</p>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="sr-only"
                    />
                    <div className="w-11 h-6 bg-[#48d480] rounded-full">
                      <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
                    </div>
                  </div>
                </div>

                {/* Performance Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-[#0f2d52]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Performance Cookies
                    </h4>
                    <p className="text-sm text-gray-500">Help us improve site performance and user experience</p>
                  </div>
                  <label className="relative cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.performance}
                      onChange={(e) => setPreferences({ ...preferences, performance: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${preferences.performance ? 'bg-[#48d480]' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${preferences.performance ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-[#0f2d52]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Functional Cookies
                    </h4>
                    <p className="text-sm text-gray-500">Enable enhanced features and personalization</p>
                  </div>
                  <label className="relative cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${preferences.functional ? 'bg-[#48d480]' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${preferences.functional ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>

                {/* Targeting Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-[#0f2d52]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Targeting Cookies
                    </h4>
                    <p className="text-sm text-gray-500">Used to show relevant advertisements</p>
                  </div>
                  <label className="relative cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.targeting}
                      onChange={(e) => setPreferences({ ...preferences, targeting: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${preferences.targeting ? 'bg-[#48d480]' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${preferences.targeting ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>
              </div>

              {/* Settings Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptSelected}
                  className="flex-1 bg-[#48d480] hover:bg-[#3bc070] text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-[#0f2d52] hover:bg-[#1a3a5c] text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Accept All
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
